<template>
<date-picker
    v-model='model'
    v-click-outside='closeDatePicker'
    class='qa-mx-datepicker'
    :class='[
        datePickerClass,
        `mx-datepicker--${pickerStyle}`,
        `mx-datepicker-reserve-slot-color--${reservedSlotsColor}`,
        {
            "mx-week-mode": isWeekMode,
            "mx-month-mode": isMonthMode,
            "mx-only-buttons-mode": onlyButtons,
            "mx-slot-customise-mode": isSlotCustomisedMode,
            "mx-slot-centered-mode": isCentered,
            "mx-hide-icon": hideIcon,
            "mx-alt-view": altView,
            "mx-big-style": bigStyle,
            "mx-with-reserved-slots": reservedSlots && reservedSlots.length,
            "mx-list-open": listOpen,
            "mx-datepicker--open-on-top": openOnTop,
        }
    ]'
    :type='localType'
    :lang='propsLang'
    :clearable='false'
    :disabled='isDisabled'
    :format='format'
    :confirmText='"Apply"'
    :editable='editStatus'
    :range='needToFixRange || range'
    :needToFixRange='needToFixRange'
    :confirm='isNeedConfirm'
    :placeholder='placeholder'
    :shortcuts='false'
    :calendarPanelKey='calendarPanelKey'
    :appendToBody='appendToBody'
    :popupStyle='popupStyle'
    :time-picker-options='propsTimePickerOptions'
    :inputClass='[propsInputClass, "mx-input"]'
    :inputPostfix='propsInputPostfix'
    :hideInput='hideInput'
    :altView='altView'
    :showWeeksNumbers='showWeeksNumbers'
    :reservedSlots='reservedSlots'
    :bigStyle='bigStyle'
    :disabledDays='disabledDate'
    :disabledTime='disabledTime'
    :markDates='markDates'
    :firstDayOfWeek='firstDayOfWeek'
    :defaultValue='defaultValue'
    @change-calendar-month='data => $emit("change-calendar-month", data)'
    @confirm='handleConfirm'
    @focus='onFocus'
    @blur='onBlur'
    @selectDate='onSelectDate'
    @clickOnMarkedDate='clickOnMarkedDateHanler'
    @popupOpen='onPopupOpen'>
    <template #header>
        <div v-if='selectMode'
             class='h-ph-20'
             style='max-width: 300px'
             :class='selectModePanelClass'>
            <FwMenu
                v-if='selectMode'
                class='h-mt-20 h-mh--10'
                type='buttons'
                itemFontSize='12'
                :menuItems='menuItemsLikeDatePicker'
                @click='changeActiveLink'>
            </FwMenu>

            <div v-if='selectMode && isSlotMode'
                 class='fw-dp-shortcuts-wrapper'
                 :class='{ "h-mb-20": onlyButtons }'>
                <div v-for='button in filteredSlotMenuButtons'
                     class='fw-dp-shortcuts-button'>
                    <FwButton
                        borderRadiusType='small-border'
                        size='flex'
                        fontSize='14px'
                        :active='button.active'
                        :styles='{ "padding": "5px" }'
                        color='transparent'
                        @click='setActiveSlotMenu(button)'>
                        {{ button.text }}
                    </FwButton>
                </div>
            </div>
        </div>
    </template>
    <slot slot='calendar-icon'
          name='icon'>
        <template v-if='iconName'>
            <FwIcon
                class='h-flex'
                :icon='iconName'
                :size='iconSize'
                :color='propsIconColor || iconColor'>
            </FwIcon>
        </template>
    </slot>
</date-picker>
</template>

<script lang="ts">
import { Component, Mixins, Prop, Emit, Watch } from 'vue-property-decorator';
// @ts-ignore-next-line
import DatePicker from '../../libs/datepicker';
import FwFieldMixin from './interfaces/FwFieldMixin';
const vClickOutside = require('v-click-outside');
/* eslint-disable no-unused-vars */
import { FwMenuItemType } from '../../types/Menu';

const DATEPICKER_SLOT_TYPE = 'slot';
const DATEPICKER_DAY_TYPE = 'day';
const DATEPICKER_WEEK_TYPE = 'week';
const DATEPICKER_MONTH_TYPE = 'month';
const DATEPICKER_TIME_TYPE = 'time';

type slotMenuType = {
    text: string,
    value?: Array<Date> | string,
    active: boolean
    id: string
}

const menuItemsLikeDatePicker: Array<FwMenuItemType> = [
    {
        id: 1,
        title: DATEPICKER_SLOT_TYPE.toUpperCase(),
        type: DATEPICKER_SLOT_TYPE,
        active: true,
    },
    {
        id: 2,
        title: DATEPICKER_DAY_TYPE.toUpperCase(),
        type: DATEPICKER_DAY_TYPE,
    },
    {
        id: 3,
        title: DATEPICKER_WEEK_TYPE.toUpperCase(),
        type: DATEPICKER_WEEK_TYPE,
    },
    {
        id: 4,
        title: DATEPICKER_MONTH_TYPE.toUpperCase(),
        type: DATEPICKER_MONTH_TYPE,
    },
    {
        id: 5,
        title: DATEPICKER_TIME_TYPE.toUpperCase(),
        type: DATEPICKER_TIME_TYPE,
    },
];

const getDeltaDaysDate = (delta: number): Date => {
    const date = new Date();
    return new Date(date.setDate(date.getDate() + delta));
};

const getPreviousMonthDates = (): Array<Date> => {
    const date = new Date();
    return [
        new Date(date.getFullYear(), date.getMonth() - 1, 1),
        new Date(date.getFullYear(), date.getMonth(), 0),
    ];
};

const getCurrentWeekDates = (fromDate?: Date | undefined): Array<Date> => {
    function firstDayOfWeek(date: Date) {
        return new Date(date.setDate(date.getDate() - date.getDay() + 1));
    }

    function lastDayOfWeek(date: Date) {
        return new Date(date.setDate(date.getDate() - date.getDay() + 7));
    }
    const date = fromDate || new Date();
    return [firstDayOfWeek(new Date(date.getTime())), lastDayOfWeek(new Date(date.getTime()))];
};

const CUSTOMIZED_TEXT: string = 'CUSTOMIZED';

const SLOT_MENU: Array<slotMenuType> = [
    {
        text: '7 LAST DAYS',
        id: `7-last-days`,
        value: [getDeltaDaysDate(-6), new Date()],
        active: false,
    },
    {
        text: 'LAST WEEK',
        id: `last-week`,
        value: getCurrentWeekDates(),
        active: false,
    },
    {
        text: '30 LAST DAYS',
        id: `30-days`,
        value: [getDeltaDaysDate(-29), new Date()],
        active: false,
    },
    {
        text: 'LAST MONTH',
        id: `last-month`,
        value: getPreviousMonthDates(),
        active: false,
    },
    {
        text: '90 LAST DAYS',
        id: `90-days`,
        value: [getDeltaDaysDate(-89), new Date()],
        active: false,
    },
    {
        text: CUSTOMIZED_TEXT,
        id: `time`,
        active: false,
    },
];

const STYLE_BASE: string = 'base';
const STYLE_FORM: string = 'form';
const STYLE_TIME: string = 'time';
const STYLE_FORM_LIGHT: string = 'form-light';
const STYLE_INPUT: string = 'input';
const STYLE_STATIC: string = 'static';
const SASS_SIMPLE: string = 'sass-simple';
const AVAILABLE_STYLES: Array<string> = [
    STYLE_BASE,
    STYLE_FORM,
    STYLE_TIME,
    STYLE_FORM_LIGHT,
    STYLE_INPUT,
    STYLE_STATIC,
    SASS_SIMPLE,
];

type ReservedSlotsType = {
    date: Date,
    recurrenceType?: string,
    dayList?: Array<string>,
    endRecurrence?: boolean,
    endRecurrenceDate?: Date,
};

@Component({
    components: {
        DatePicker,
    },
    directives: {
        clickOutside: vClickOutside.directive,
    },
})
export default class FwDatepicker extends Mixins(FwFieldMixin) {
    /**
     * Boolean prop to be able to select date range.
     */
    @Prop({ type: Boolean, default: false }) readonly propsRange!: boolean;
    @Prop({ type: Boolean, default: false }) readonly editStatus!: boolean;
    @Prop({ type: String }) readonly propsType!: string | null;
    @Prop({ type: String, default: 'fr' }) readonly propsLang!: string | null;
    // @ts-ignore
    @Prop({ type: String, default: 'Select period' }) readonly placeholder!: string;
    @Prop({ type: String, default: null }) readonly datePickerClass!: string;
    @Prop({ type: String, default: '' }) readonly propsInputPostfix!: string;
    @Prop({ type: String, default: null }) readonly propsFormat!: string;
    @Prop({ type: String, default: null }) readonly propsInputClass!: string;
    @Prop({ type: String, default: null }) readonly propsIconColor!: string;
    @Prop({ type: String, default: 'calendar' }) readonly iconName!: string;
    @Prop({ type: String, default: '16' }) readonly iconSize!: string;
    @Prop({ type: Boolean, default: false }) readonly selectMode!: boolean;
    @Prop({ type: Boolean, default: false }) readonly appendToBody!: boolean;
    @Prop({ type: Boolean, default: false }) readonly onlyButtons!: boolean;
    @Prop({ type: Boolean, default: true }) readonly isCentered!: boolean;
    @Prop({ type: Boolean, default: false }) readonly propsConfirm!: boolean;
    @Prop({ type: Boolean, default: false }) readonly hideIcon!: boolean;
    @Prop({ type: Boolean, default: false }) readonly iconRight!: boolean;
    @Prop({ type: Boolean, default: false }) readonly customArrowColor!: boolean;
    @Prop({ type: Boolean, default: false }) readonly hideInput!: boolean;
    @Prop({ type: Boolean, default: false }) readonly altView!: boolean;
    @Prop({ type: Boolean, default: false }) readonly bigStyle!: boolean;
    @Prop({ type: Boolean, default: false }) readonly openOnTop!: boolean;
    @Prop({ type: Boolean, default: false }) readonly showWeeksNumbers!: boolean;
    @Prop({ type: Date, default: () => new Date() }) readonly defaultValue!: Date;
    @Prop({ type: [Function, Object], default: () => false }) readonly disabledDate!: Function;
    @Prop({ type: [Function, Object], default: () => false }) readonly disabledTime!: Function;
    @Prop({ type: [Function, Object], default: () => false }) readonly markDates!: Function;
    @Prop({ type: Number, default: 1 }) readonly firstDayOfWeek!: number;
    @Prop({ type: Array, default: () => [] }) readonly filterSlots!: Array<string>;
    @Prop({ type: Array, default: () => [] }) readonly filteredSlotMenu!: Array<number>;
    @Prop({ type: Object, default: () => {} }) readonly translateSlotMenuTitles!: { [key: number]: string };
    @Prop({ type: Array, default: null }) readonly reservedSlots!: Array<ReservedSlotsType> | null;
    @Prop({ type: Object, default: null }) readonly propsTimePickerOptions!: { [key: string]: string };
    @Prop({ type: Object, default: () => ({ 'right': 0, 'left': 0 }) }) readonly popupStyle!: { [key: string]: string };
    @Prop({ type: String, default: null }) readonly selectModePanelClass!: string;
    @Prop({ type: String, default: STYLE_BASE, validator: (type: string) => AVAILABLE_STYLES.includes(type) })
    readonly pickerStyle!: string;
    @Prop({ type: String, default: 'green', validator: (type: string) => ['red', 'green'].includes(type) })
    readonly reservedSlotsColor!: string;

    menuItemsLikeDatePicker: Array<FwMenuItemType> = menuItemsLikeDatePicker.filter(
        item => !this.filteredSlotMenu.includes(item.id)
    ).map(item => {
        if (this.translateSlotMenuTitles && this.translateSlotMenuTitles[item.id]) {
            item.title = this.translateSlotMenuTitles[item.id];
        }
        return item;
    });
    slotMenuButtons: Array<slotMenuType> = SLOT_MENU;
    localRange: boolean = false;
    listOpen: boolean = false;
    isPreviousTypeRange: null | boolean = null;
    calendarPanelKey: number = 1;

    get isSlotMode(): boolean {
        return this.activeMode === DATEPICKER_SLOT_TYPE;
    }

    get isDayMode(): boolean {
        return this.activeMode === DATEPICKER_DAY_TYPE;
    }

    get isWeekMode(): boolean {
        return this.activeMode === DATEPICKER_WEEK_TYPE;
    }

    get isMonthMode(): boolean {
        return this.activeMode === DATEPICKER_MONTH_TYPE;
    }

    get isTimeMode(): boolean {
        return this.activeMode === DATEPICKER_TIME_TYPE;
    }

    get isPickerStyleBig(): boolean {
        return this.activeMode === DATEPICKER_MONTH_TYPE;
    }

    get isSlotCustomisedMode(): boolean {
        return Boolean(this.isSlotMode && this.activeSlotMenu && this.activeSlotMenu.text === CUSTOMIZED_TEXT);
    }

    get activeMode(): string {
        if (!this.selectMode) {
            return this.propsType || DATEPICKER_DAY_TYPE;
        }
        const activeMenu: FwMenuItemType | undefined = this.menuItemsLikeDatePicker.find(menu => menu.active);
        return activeMenu ? activeMenu.type : DATEPICKER_DAY_TYPE;
    }

    get localType(): string {
        if (!this.selectMode) {
            return this.propsType || 'date';
        }
        if (this.isDayMode) {
            return 'date';
        }
        if (this.isWeekMode) {
            return 'date';
        }
        if (this.isMonthMode) {
            return 'month';
        }
        if (this.isTimeMode) {
            return 'time';
        }
        return 'date';
    }

    get format(): string | null {
        if (this.propsFormat) {
            return this.propsFormat;
        }
        if (this.isMonthMode) {
            return 'YYYY-MM';
        }
        if (this.isWeekMode) {
            return 'MMM DD, YYYY';
        }
        if (this.isTimeMode) {
            return 'hh:mm';
        }
        return 'MMM DD, YYYY';
    }

    get range(): boolean {
        return Boolean(this.propsRange || this.localRange || this.isSlotMode);
    }

    get isDisabled() {
        return this.FwField.disabled;
    }

    get iconColor(): string {
        return (this.pickerStyle === STYLE_FORM &&
            this.pickerStyle === STYLE_FORM_LIGHT &&
            this.pickerStyle === STYLE_TIME) || this.customArrowColor ? '#203f6a' : '#fff';
    }

    get activeSlotMenu(): slotMenuType | undefined {
        return this.isSlotMode ? this.slotMenuButtons.find(item => item.active) : undefined;
    }

    get filteredSlotMenuButtons(): Array<slotMenuType> {
        return this.slotMenuButtons.filter(item => !this.filterSlots.includes(item.id));
    }

    async setActiveSlotMenu(item: slotMenuType) {
        const currentMenuIndex = this.slotMenuButtons.findIndex(menu => menu.text === item.text);
        for (let i = 0; i < this.slotMenuButtons.length; i++) {
            this.slotMenuButtons[i].active = currentMenuIndex === i;
        }
        this.slotMenuButtons = [...this.slotMenuButtons];
        this.model = item.value;

        await this.$nextTick();
        if (this.onlyButtons && !this.isSlotCustomisedMode) {
            this.closeDatePicker();
            this.handleConfirm();
        }
    }

    async changeActiveLink(item: FwMenuItemType): Promise<void> {
        this.isPreviousTypeRange = this.range;
        for (let i = 0; i < menuItemsLikeDatePicker.length; i++) {
            this.$set(menuItemsLikeDatePicker[i], 'active', false);
        }
        const link: FwMenuItemType | undefined = menuItemsLikeDatePicker.find(menuItem => menuItem.id === item.id);
        if (link) {
            this.$set(link, 'active', true);
        }
        this.menuItemsLikeDatePicker = Object.assign(
            this.menuItemsLikeDatePicker,
            menuItemsLikeDatePicker.find(menuItem => menuItem.id === item.id)
        );
        this.rerenderDatePicker();
    }

    get isNeedConfirm(): boolean {
        if (this.propsConfirm) {
            return true;
        }
        if (this.onlyButtons) {
            return this.isSlotCustomisedMode;
        }
        return this.range;
    }

    get needToFixRange(): boolean {
        return this.isWeekMode && (Array.isArray(this.model) || Array.isArray(this.value));
    }

    rerenderDatePicker() {
        // It is fix vue2-datepicker reactive type change!
        this.forceUpdate();
        this.updateStartValue();
    }

    updateStartValue(value?: string | Array<string> | null) {
        if (this.range) {
            this.model = (Array.isArray(value) && value) || [];
        } else {
            this.model = (typeof value === 'string' && value) || (typeof value === 'object' && value) || null;
        }
    }

    forceUpdate(): void {
        this.calendarPanelKey += 1;
    }

    getDayDateNearNight(date: Date): Date {
        const localDate = new Date(date.getTime());
        localDate.setHours(23, 59, 0, 0);
        return localDate;
    }

    @Emit('selectDate')
    onSelectDate(value: Date) {
        if (this.isWeekMode) {
            this.localRange = true;
            this.model = getCurrentWeekDates(value);
        }
        if (!this.isNeedConfirm) {
            this.handleConfirm();
        }
        this.asRangeHandler(this.model);
    }

    @Emit('asRange')
    asRangeHandler(value: Date | Array<Date>): Array<Date> | undefined {
        if (Array.isArray(value)) return value;
        if (this.isDayMode) {
            return [value];
        }
        if (this.isMonthMode) {
            const clone = new Date(value.getTime());
            const lastDayOfMonth = new Date(clone.getFullYear(), clone.getMonth() + 1, 0);
            return [value, lastDayOfMonth];
        }
    }

    @Emit('clickOnMarkedDate')
    clickOnMarkedDateHanler(value: Date) {}

    onPopupOpen() {
        this.listOpen = true;
        const value = this.model;
        this.localRange = false;
        this.updateStartValue(value);
    }

    @Emit('closePopup')
    closeDatePicker() {
        this.listOpen = false;
        // @ts-ignore-next-line HACK
        this.$children[0].popupVisible = false;
    }

    @Emit('confirm')
    handleConfirm() {}

    @Watch('model')
    localModelHandler(value: Date, prevValue: null | Date) {
        if (Object.prototype.toString.call(value) === '[object Date]') {
            if (Number.isNaN(value.getTime()) && prevValue) {
                this.model = new Date(prevValue);
            }
        }
        if (value) {
            this.listOpen = false;
        }
    }

    created() {
        if (this.selectMode) {
            const value = JSON.parse(JSON.stringify(this.value));
            if (value && Array.isArray(value)) {
                this.changeActiveLink(menuItemsLikeDatePicker.find(item => item.type === DATEPICKER_WEEK_TYPE)!);
                this.updateStartValue(value);
            }
        }
    }
}
</script>

<style lang="sass">
.mx-input-append
    align-items: center
    justify-content: center
    display: flex
    position: static
    margin-right: 10px

.mx-week-number__wrapper
    display: flex
    justify-content: flex-end
    flex-direction: column

    .mx-week-number
        display: flex
        align-items: center
        justify-content: center

        &__inner
            width: 22px
            height: 22px
            background-color: rgba(33,63,107,0.08)
            color: $main-gray
            font-size: 12px
            border-radius: 5px
            display: flex
            align-items: center
            justify-content: center

.mx-input-wrapper
    display: flex
    color: #fff
    height: 100%
    align-items: center
    flex-direction: row-reverse
    width: 100%

    input
        flex: 1
        display: flex

.mx-datepicker
    width: 100%
    height: 40px
    border-radius: 10px
    display: flex
    align-items: center
    justify-content: center
    padding: 0 15px

    &--hide
        min-height: 270px
        .mx-datepicker-popup
            margin-top: 0

    &--base
        background-color: $aqua-marine

        .mx-input
            color: #fff

            &::-webkit-input-placeholder
                color: #fff
                font-weight: 300

            &::-moz-placeholder
                color: #fff
                font-weight: 300

            &:-ms-input-placeholder
                color: #fff
                font-weight: 300

            &:-moz-placeholder
                color: #fff
                font-weight: 300

    &--form, &--input, &--form-light
        .mx-input
            color: $dark-blue

        .mx-datepicker-popup
            background: $middle-gray
            color: $dark-gray
            padding: 0

        .mx-calendar
            padding: 10px
            width: 100%

        .mx-calendar-header
            color: $dark-blue

        .mx-time-list
            border: none
            padding-right: 5px

        .mx-calendar-content
            .cell
                &:hover
                    background-color: lighten($main-green, 20)
                    color: #fff
                border-radius: 10px !important

        .mx-calendar
            color: $dark-blue
            svg
                fill: $dark-blue

    &--form
        background-color: $middle-gray
        .mx-input

            &::-webkit-input-placeholder
                color: $dark-blue
                font-weight: 300

            &::-moz-placeholder
                color: $dark-blue
                font-weight: 300

            &:-ms-input-placeholder
                color: $dark-blue
                font-weight: 300

            &:-moz-placeholder
                color: $dark-blue
                font-weight: 300

    &--form-light
        background-color: $light-gray
        border: 2px solid $light-gray
        .mx-input
            &::-webkit-input-placeholder
                color: $main-gray
                font-weight: 300

            &::-moz-placeholder
                color: $main-gray
                font-weight: 300

            &:-ms-input-placeholder
                color: $main-gray
                font-weight: 300

            &:-moz-placeholder
                color: $main-gray
                font-weight: 300

        .mx-datepicker-popup
            background: $light-gray
            color: $dark-gray
            padding: 0

    &--sass-simple
        background-color: transparent

        .mx-datepicker-popup
            min-width: 284px

        .mx-input, .mx-input::placeholder
            color: $alt-blue
            font-size: 12px
            font-weight: 500

    &--input
        height: $input-h
        border: 2px solid $middle-gray
        background-color: $middle-gray

        .mx-input
            color: $dark-blue

            &::-webkit-input-placeholder
                color: $main-gray
                font-weight: 300

            &::-moz-placeholder
                color: $main-gray
                font-weight: 300

            &:-ms-input-placeholder
                color: $main-gray
                font-weight: 300

            &:-moz-placeholder
                color: $main-gray
                font-weight: 300

        &:hover
            background-color: #fff

    &--static
        .mx-calendar, .mx-datepicker-popup
            background-color: transparent !important

        .mx-calendar
            padding: 0

        .mx-calendar-header
            color: $dark-blue

        .mx-calendar-content
            margin-top: 10px

        .mx-panel-date
            th
                font-family: GothamRounded, Arial, serif
                font-size: 12px
                color: $dark-blue
                font-weight: 500

        .mx-calendar
            color: $dark-blue
            svg
                fill: $dark-blue

        &.mx-alt-view
            .mx-current-month-year
                position: absolute
                left: 10px

            .mx-icon-last-month
                position: absolute
                right: 50px

        .mx-icon-last-month, .mx-icon-next-month
            svg
                fill: #203F6B

                &:hover
                    fill: $dark-blue

        .mx-icon-last-year, .mx-icon-next-year
            svg
                fill: #BEC7DD

                &:hover
                    fill: $dark-blue
    &--time
        .mx-calendar
            width: 100%
            padding: 0
            &-content .cell.actived
                margin: 0
            .mx-time-list
                margin: 10px 0
        .mx-datepicker-popup
            margin-top: 40px
        .mx-input-wrapper input
            font-weight: 500
            text-align: left
            color: $dark-blue

    .mx-calendar-panel-time .mx-calendar-header
        display: none

.mx-input
    padding: 0
    border: none
    box-shadow: none
    height: 22px
    background-color: transparent

.mx-datepicker-popup
    top: 0 !important
    margin-top: 60px
    box-shadow: none
    border: none
    border-radius: 20px
    overflow: hidden
.mx-calendar-content
    .cell
        font-family: GothamRounded, Arial, serif
        font-size: 12px
        font-weight: 500
        position: relative
        &.actived
            margin: 20px
            background-color: $aqua-marine
            border-radius: 10px
            color: #fff
            .fw-datepicker-reserved
                background: #fff
    .mx-panel-year
        .cell.actived
            margin: 1px 5%
    .mx-panel-month
        .cell.actived
            margin: 1px 1%

.mx-datepicker-range .mx-calendar-panel-date .cell.actived
    border-radius: 0 10px 10px 0

.mx-datepicker-range .mx-calendar-panel-date:first-child .cell.actived
    border-radius: 10px 0 0 10px

.mx-calendar-content .cell.inrange
    background-color: rgba(39,219,189,0.12)

.mx-panel-date td.today
    color: $aqua-marine

.mx-calendar-content .cell.disabled
    color: #cecece

.mx-range-wrapper
    width: 100%
    display: flex
    @include media('<=phone')
        width: auto

.mx-calendar
    padding: 30px

    @include media('<=phone')
        float: none

.mx-datepicker-btn-confirm
    border: none
    color: $alt-blue

    &:hover
        color: $alt-blue

.mx-panel-date td, .mx-panel-date th
    font-size: 14px
    color: $dark-blue
    line-height: 17px

.mx-calendar-content
    width: 100% !important
    &.mx-calendar-content--with-weeks
        display: flex

.mx-datepicker-range
    .mx-calendar-panel-date
        .cell
            &.actived
                border-radius: 50% !important
                overflow: visible

.mx-calendar.mx-calendar-panel-date
    &:first-of-type
        .cell.actived
            position: relative
            &:before
                content: ''
                width: 50%
                position: absolute
                right: 0
                top: 0
                background-color: rgba(39,219,189,0.12)
                height: 100%
    &:last-of-type
        .cell.actived
            position: relative
            &:before
                content: ''
                width: 50%
                position: absolute
                left: 0
                top: 0
                background-color: rgba(39,219,189,0.12)
                height: 100%

.mx-calendar-header
    color: $main-green

    svg
        fill: $main-green

.mx-current-month
    text-transform: uppercase

.mx-current-month-year
    font-family: GothamRounded, Arial, serif
    font-weight: 600
    .mx-current-month
        margin-right: 5px

.mx-panel-date th
    color: $main-gray

.mx-datepicker-footer
    .mx-datepicker-btn
        font-size: 15px
        color: $main-green

.fw-dp-shortcuts-wrapper
    margin-top: 20px
    display: flex
    flex-wrap: wrap

.fw-dp-shortcuts-button
    flex: 1 0 50%
    margin: 3px 0

.mx-week-mode
    .mx-calendar-content
        tbody
            tr
                &:hover
                    background-color: #eaf8fe

.mx-month-mode
    .mx-calendar-content .cell.actived
        border-radius: 10px

.mx-calendar-header > a
    margin: 0 2px

    font-weight: 500
    font-size: 16px

.mx-only-buttons-mode
    &:not(.mx-slot-customise-mode)
        .mx-range-wrapper
            display: none !important
    .mx-datepicker-popup
        overflow: visible
        &:after
            content: ''
            position: absolute
            top: -20px
            margin: auto
            right: 50%
            margin-right: -5px
            border: 10px solid transparent
            border-bottom: 10px solid #fff

.mx-slot-centered-mode
    &.mx-slot-customise-mode
        .mx-range-wrapper
            width: 600px
            background-color: #fff
            left: -150px
            position: absolute
            border-radius: 20px

        .mx-datepicker-footer
            position: relative
            bottom: -318px
            background: #fff
            border-radius: 0 0 10px 10px

.mx-hide-icon
    .mx-input-append
        display: none !important

.mx-big-style
    &.mx-datepicker--hide
        height: 330px
    .mx-panel-date td, .mx-panel-date th, .mx-week-number
        width: 42px
        height: 42px
        font-weight: bold

    .mx-panel-date th
        color: $main-gray

    .mx-week-number
        &__inner
            width: 26px
            height: 26px

        &__wrapper
            margin-left: 20px

    .mx-calendar-content
        height: auto

    .mx-icon-next-month, .mx-icon-last-month
        svg
            fill: $main-green !important

    .mx-current-month-year, .mx-icon-last-month
        color: $main-green

    .mx-icon-last-year, .mx-icon-next-year
        display: none !important

.fw-datepicker-reserved
    width: 6px
    height: 6px
    position: absolute
    left: 0
    right: 0
    bottom: 2px
    margin: auto
    background-color: $main-green
    border-radius: 50%

.mx-big-style
    .fw-datepicker-reserved
        bottom: 5px

.mx-datepicker-reserve-slot-color--green
    .fw-datepicker-reserved
        background-color: $main-green

.mx-datepicker-reserve-slot-color--red
    .fw-datepicker-reserved
        background-color: $cancel-red

.mx-hide-icon
    .mx-input-append
        display: flex!important
        position: absolute
        right: -25px

.mx-list-open-------disable-hack
    .mx-input-append
        transition: transform 0.2s ease-in-out
        transform: rotate(180deg)

.mx-datepicker--open-on-top
    .mx-datepicker-popup
        bottom: 50px !important
        top: auto !important
</style>
